/**
 * Auth Provider - Pure state provider for authentication
 *
 * Responsibilities:
 * - Manage auth state (user, session, loading)
 * - Listen to auth state changes
 * - Provide auth context to children
 * - Throttle session activity logs
 *
 * Note: Navigation logic has been moved to route guards in App.tsx
 * This decouples auth from routing, enabling reuse in non-routing contexts
 *
 * Migration: Moved from src/hooks/use-auth.tsx to src/providers/AuthProvider.tsx
 * - Added proper cleanup for auth listeners
 * - Conditional debug logging (respects import.meta.env.DEV)
 * - Throttled session activity logs
 * - Ref-based guard for StrictMode-safe singleton mounting
 * - Memoized listener callback to prevent re-subscriptions
 *
 * Dev Console Spam Elimination:
 * - Throttle guard logs removed (lines 80-81): No "throttled" messages
 * - Duplicate listener guard logs removed (lines 131-132): No "already set up" messages
 * - React StrictMode disabled in dev via vite.config.ts to prevent double-mounts
 * - Functionality preserved: throttling and duplicate prevention still work
 *
 * Verification Steps:
 * 1. Dev console should be clean on load/navigation/auth page
 * 2. One session activity RPC per browser session (5min cooldown)
 * 3. Zero "throttled" or "already set up" console lines
 * 4. Production build unchanged/silent (NODE_ENV=production)
 * 5. React devtools shows single AuthProvider instance
 * 6. No leaked listeners (check cleanup on unmount)
 */

import {
  useState,
  useEffect,
  useRef,
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useCallback,
} from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabaseClient';
// Import directly from auth folder to avoid circular dependency through barrel file
import { useSessionTracking } from '@/hooks/auth/use-session-tracking';
import { createLogger } from '@/lib/structuredLogger';

// Extend Window interface for auth debug flag
declare global {
  interface Window {
    __AUTH_LOG_SUPPRESSED?: boolean;
  }
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  accessVersion: number;
  logSessionActivity: (activityType: string, metadata?: Record<string, unknown>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  accessVersion: 0,
  logSessionActivity: async () => {},
});

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);

// Session-level throttling constants
const SESSION_LOG_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes
const SESSION_LOG_STORAGE_KEY = 'auth_session_log_last_call';
const logger = createLogger('AuthProvider');

// Helper to check if debug logging should be enabled
const shouldLogDebug = (): boolean => {
  return import.meta.env.DEV && !(typeof window !== 'undefined' && window.__AUTH_LOG_SUPPRESSED);
};

const isSessionActivityLoggingEnabled = (): boolean => {
  return import.meta.env.VITE_ENABLE_SESSION_ACTIVITY_LOGGING === 'true';
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessVersion, setAccessVersion] = useState(0);
  const hasResolvedInitialSessionRef = useRef(false);

  // Throttled log_session_activity function - once per session (localStorage + 5min cooldown)
  const logSessionActivity = useCallback(
    async (activityType: string, metadata?: Record<string, unknown>) => {
      if (!user || !session) return;
      if (!isSessionActivityLoggingEnabled()) return;

      // Check localStorage for last call timestamp
      const storageKey = `${SESSION_LOG_STORAGE_KEY}_${session.access_token.slice(-8)}`;
      const now = Date.now();

      try {
        const lastCallStr = localStorage.getItem(storageKey);
        if (lastCallStr) {
          const lastCall = parseInt(lastCallStr, 10);
          const timeSinceLastCall = now - lastCall;

          // If called within cooldown period, skip
          if (timeSinceLastCall < SESSION_LOG_COOLDOWN_MS) {
            // Silenced dev spam: Throttle guard log removed to eliminate console noise
            // Functionality preserved - throttling still works, just no debug output
            return;
          }
        }

        const rpcMetadata = JSON.parse(JSON.stringify(metadata || {}));

        // Call RPC
        await supabase.rpc('log_session_activity', {
          p_user_id: user.id,
          p_activity_type: activityType,
          p_metadata: rpcMetadata,
        });

        // Update localStorage with current timestamp
        localStorage.setItem(storageKey, now.toString());

        if (shouldLogDebug()) {
          logger.debug('Session activity logged', { activityType });
        }
      } catch (error) {
        // Only log in development if not suppressed
        if (shouldLogDebug()) {
          logger.debug('Session activity log error', { error, activityType });
        }
      }
    },
    [user, session],
  );

  // Enable session activity tracking
  useSessionTracking(user, logSessionActivity);

  // Memoize the auth state change callback to prevent re-subscriptions
  const handleAuthStateChange = useCallback(
    (event: string, session: Session | null) => {
      if (shouldLogDebug()) {
        logger.debug('Auth state changed', {
          event,
          hasSession: !!session,
          userId: session?.user?.id,
          pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
        });
      }

      setSession(session);
      setUser(session?.user ?? null);
      // Revalidate authority even when Auth rotates a session for the same owner.
      setAccessVersion((version) => version + 1);

      // INITIAL_SESSION fires once on subscription setup with the cached session.
      // Using it as the sole loading gate avoids a separate getSession() call that
      // would compete for the same navigator lock and trigger the 5 000 ms warning.
      if (
        event === 'INITIAL_SESSION' ||
        event === 'SIGNED_IN' ||
        event === 'SIGNED_OUT' ||
        event === 'TOKEN_REFRESHED' ||
        event === 'USER_UPDATED'
      ) {
        hasResolvedInitialSessionRef.current = true;
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    let isMounted = true;

    if (shouldLogDebug()) {
      logger.debug('Setting up auth listener');
    }

    // onAuthStateChange fires INITIAL_SESSION synchronously with the cached
    // session, then TOKEN_REFRESHED / SIGNED_IN / SIGNED_OUT as they happen.
    // This single call replaces both onAuthStateChange + getSession, avoiding
    // the lock contention that caused the 5 000 ms auth-token lock warning.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(handleAuthStateChange);

    const resolveSessionSnapshot = async () => {
      if (hasResolvedInitialSessionRef.current) return;

      try {
        const { data, error } = await supabase.auth.getSession();

        if (!isMounted || hasResolvedInitialSessionRef.current) return;

        if (error) {
          if (shouldLogDebug()) {
            logger.debug('Auth session fallback failed', { error });
          }
        } else {
          setSession(data.session);
          setUser(data.session?.user ?? null);
        }
      } catch (error) {
        if (shouldLogDebug()) {
          logger.debug('Auth session fallback threw', { error });
        }
      } finally {
        if (isMounted && !hasResolvedInitialSessionRef.current) {
          hasResolvedInitialSessionRef.current = true;
          setLoading(false);
        }
      }
    };

    // Safety net: if INITIAL_SESSION never fires on a platform/browser storage
    // edge case, resolve from the current session snapshot without warning spam.
    const safetyTimeout = setTimeout(() => {
      void resolveSessionSnapshot();
    }, 5_000);

    return () => {
      isMounted = false;
      clearTimeout(safetyTimeout);
      try {
        subscription.unsubscribe();
      } catch (error) {
        if (shouldLogDebug()) {
          logger.debug('Error during listener cleanup', { error });
        }
      }
    };
  }, [handleAuthStateChange]);

  const contextValue = useMemo(
    () => ({
      user,
      session,
      loading,
      accessVersion,
      logSessionActivity,
    }),
    [user, session, loading, accessVersion, logSessionActivity],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
